import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useRecording } from '../useRecording';
import { convertLengthBytes, mapRange } from 'lib/clinic/library';
import { useCreateProjectInfoStore } from 'store/local/projectCreateinfo';
import { ProjectActions } from 'store/actionCreators';
import { useParams } from 'react-router-dom';
import { useShallowAppSelector } from 'store/hooks';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { ITranscription } from 'lib/types/utilTypes';
import { TVoiceMemo } from 'lib/types/clinic/projectTypes';

export const useCreateProjectMemo = () => {
  // SECTION: declare server state
  const { projectData, projectSuccess, editProjectSuccess, loadedSource } = useShallowAppSelector(
    state => ({
      projectData: state.project.project.data,
      projectSuccess: state.project.project.success,
      editProjectSuccess: state.project.editProject.success,
      loadedSource: state.app.loadedSource,
    }),
  );

  // SECTION: declare local state
  const { historyOpen, setHistoryOpen } = useProjectDetailStore();

  const {
    senderMemo,
    setSenderMemo,
    voiceMemoList,
    setVoiceMemoList,
    reset: createProjectInfoReset,
    setMultiple: setCreateProjectInfoMultiple,
  } = useCreateProjectInfoStore();

  const {
    isRecording,
    isTranscriptionLoading,
    recordedSummary,
    recordedText,
    recordedDate,
    recordedList,
    recordingTime,
    decibel,
    timeProgress,
    onStartRecording,
    onStopRecording,
  } = useRecording();

  // SECTION: declare variable
  const { pcode: projectCode } = useParams() as { pcode: string };
  const [isOpenMicrophoneModal, setIsOpenMicrophoneModal] = useState(false);
  const [isMemoEdit, setIsMemoEdit] = useState(false);
  const [isMemoInit, setIsMemoInit] = useState(false);
  const [isCreatedOnlineProject, setIsCreatedOnlineProject] = useState(false);
  const projectInfo = useMemo(() => {
    if (!projectData) return;

    if (projectData) {
      return projectData?.projectInfo;
    }
  }, [projectData]);

  // SECTION: declare dependency
  useEffect(() => {
    setSenderMemo(projectInfo?.senderMemo || '');
    setVoiceMemoList(projectInfo?.voiceMemoList || []);
    setIsMemoInit(true);
    setIsCreatedOnlineProject(loadedSource === 'local' && projectInfo?.offlineYn === 0);
  }, [projectInfo]);

  useEffect(() => {
    if (senderMemo.length === 0 && voiceMemoList.length === 0) {
      setIsMemoEdit(false);
    } else {
      if (
        senderMemo !== projectInfo?.senderMemo ||
        voiceMemoList.length !== projectInfo?.voiceMemoList.length
      ) {
        setIsMemoEdit(true);
      } else {
        setIsMemoEdit(false);
      }
    }
  }, [projectInfo, senderMemo, voiceMemoList]);

  useEffect(() => {
    if (!recordedSummary) return;

    setSenderMemo(prev => prev + `\n${recordedSummary}`);
  }, [recordedSummary]);

  useEffect(() => {
    if (recordedList.length === 0) return;
    console.log('HDS] recordedList _______ ', recordedList);
    // setVoiceMemoList(recordedList);
    recordedList.map((item: ITranscription) => {
      setVoiceMemoList((draft: TVoiceMemo[]) => [
        ...draft,
        {
          voiceMemo: item.text,
          recordDate: item.recordDate,
        },
      ]);
    });
    // setVoiceMemo(prev => prev + recordedText);
  }, [recordedList]);

  useEffect(() => {
    console.log('HDS] voiceMemoList __________ ', voiceMemoList);
  }, [voiceMemoList]);

  useEffect(() => {
    if (editProjectSuccess) {
      setCreateProjectInfoMultiple({
        senderMemo: '',
        voiceMemoList: [],
      });
      setIsMemoEdit(false);
      if (loadedSource === 'server') {
        ProjectActions.fetch_project_request(projectCode);
      }
      if (loadedSource === 'local') {
        ProjectActions.fetch_project_request({
          channel: 'getProjectDetail',
          data: { projectCode: projectCode },
        });
      }
    }
  }, [editProjectSuccess]);

  // SECTION: declare function
  const handleMemoChange = (value: string) => {
    setSenderMemo(value);
  };

  const handleMicrophoneClick = (type: 'start' | 'stop') => {
    if (type === 'start') {
      onStartRecording();
    }
    if (type == 'stop') {
      onStopRecording();
    }
  };

  const handleCloseMicrophoneModal = () => {
    setIsOpenMicrophoneModal(false);
  };

  const handleConfirmMicrophoneModal = () => {
    onStartRecording();
    setIsOpenMicrophoneModal(false);
  };

  const handleEditMemo = () => {
    setIsMemoEdit(true);
  };

  const handleCancelMemo = () => {
    setIsMemoEdit(false);
    setSenderMemo(projectInfo?.senderMemo || '');
    setVoiceMemoList(projectInfo?.voiceMemoList || []);
  };

  const handleSaveMemo = () => {
    const submitData = {
      senderMemo: senderMemo,
      voiceMemoList: voiceMemoList,
    };

    if (loadedSource === 'server') {
      ProjectActions.edit_project_request({
        projectCode,
        data: {
          ...submitData,
        },
      });
    }
    if (loadedSource === 'local') {
      ProjectActions.edit_project_request({
        channel: 'updateProject',
        data: { projectCode, ...submitData },
      });
    }
  };

  const handleClickHistory = () => {
    setHistoryOpen(!historyOpen);
  };

  return {
    projectData,
    historyOpen,
    isRecording,
    isTranscriptionLoading,
    senderMemo,
    voiceMemoList,
    recordedText,
    recordingTime,
    timeProgress,
    decibel,
    isMemoEdit,
    isMemoInit,
    isCreatedOnlineProject,
    onStartRecording,
    onStopRecording,
    onMemoChange: handleMemoChange,
    onMicrophoneClick: handleMicrophoneClick,
    onEditMemo: handleEditMemo,
    onSaveMemo: handleSaveMemo,
    onCancelMemo: handleCancelMemo,
    onClickHistory: handleClickHistory,
  };
};
